import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Activity, X } from "lucide-react";

const FIELDS = [
  { key: "systolic",         label: "BP systolic",  unit: "mmHg", type: "number" },
  { key: "diastolic",        label: "BP diastolic", unit: "mmHg", type: "number" },
  { key: "pulse",            label: "Pulse",        unit: "bpm",  type: "number" },
  { key: "temperature",      label: "Temperature",  unit: "°C",   type: "number", step: "0.1" },
  { key: "respiratory_rate", label: "Resp rate",    unit: "/min", type: "number" },
  { key: "spo2",             label: "SpO₂",         unit: "%",    type: "number" },
  { key: "glucose",          label: "Glucose",      unit: "mmol/L", type: "number", step: "0.1" },
  { key: "pain_score",       label: "Pain",         unit: "/10",  type: "number", min: 0, max: 10 },
];

export function RecordVitalsDialog({ patientId, onClose, onSaved }) {
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const nonEmpty = FIELDS.filter((f) => values[f.key] !== undefined && values[f.key] !== "");
    if (nonEmpty.length === 0) { toast.error("Enter at least one vital reading"); return; }
    const payload = { patient_id: patientId, notes: notes.trim() || null };
    for (const f of nonEmpty) {
      const raw = values[f.key];
      const n = Number(raw);
      if (Number.isNaN(n)) { toast.error(`${f.label} must be numeric`); return; }
      payload[f.key] = ["temperature", "glucose"].includes(f.key) ? n : Math.round(n);
    }
    try {
      setSaving(true);
      await api.post("/vitals", payload);
      toast.success("Vitals recorded");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record vitals");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-2xl shadow-xl border border-slate-200" data-testid="record-vitals-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-[#1976D2]" /> Record vitals</div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs text-slate-500 font-medium">{f.label} <span className="text-slate-400">({f.unit})</span></label>
              <input
                data-testid={`vitals-${f.key}`}
                type={f.type}
                step={f.step}
                min={f.min}
                max={f.max}
                value={values[f.key] ?? ""}
                onChange={(e) => set(f.key, e.target.value)}
                className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
              />
            </div>
          ))}
          <div className="col-span-2 md:col-span-4">
            <label className="text-xs text-slate-500 font-medium">Notes</label>
            <input data-testid="vitals-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
          <button data-testid="vitals-submit" disabled={saving} type="submit" className="h-9 px-4 rounded-lg bg-[#1976D2] text-white text-sm font-medium disabled:opacity-60">{saving ? "Saving…" : "Save vitals"}</button>
        </div>
      </form>
    </div>
  );
}
